/**
 * Bringing work made without an account into one.
 *
 * The rule this module exists to keep: **guest work is never silently uploaded
 * and never deleted.** A learner writes for a term with no account, then makes
 * one. What happens to that work is their decision, so this module's job is to
 * describe what exists and to copy it only when asked.
 *
 * Three properties, each of which is a way this goes wrong if missed:
 *
 *   **Idempotent.** Every record is enqueued under the UUID it already has
 *   locally, and the server returns the existing row for an id it has seen. A
 *   retry after a dropped connection cannot produce a second copy of the same
 *   chord.
 *
 *   **Non-destructive.** The local record is never removed or altered by
 *   adoption. Offline use keeps working exactly as before, and a failed upload
 *   costs nothing.
 *
 *   **Partial failure is reported, not swallowed.** Uploading thirty documents
 *   over a school connection will sometimes fail at the nineteenth. Because the
 *   work is queued rather than sent directly, "failed" now means "still queued",
 *   and the engine retries it without asking the learner again.
 *
 * Adoption does not have a transport of its own. It is consent to enqueue, and
 * everything after that is the ordinary sync path — one outbox, one set of retry
 * and conflict rules. A second transport would be a second set of both, and they
 * only have to disagree once.
 */

import type { Repositories } from '../storage/repositories';
import type { LessonProgress } from '../curriculum/types';
import type { SyncEngine } from '../sync/engine';

/** Where the record of what has already been adopted lives. */
const ADOPTION_KEY = 'account-adoption';

export interface LocalDocumentSummary {
  id: string;
  title: string;
  feature: string;
  updatedAt: number;
}

export interface LocalWorkSummary {
  documents: LocalDocumentSummary[];
  lessons: string[];
  /** Everything that would be copied. Zero means there is nothing to ask about. */
  total: number;
}

interface AdoptionRecord {
  documentIds: string[];
  lessonIds: string[];
  at: number;
}

type AdoptionsByUser = Record<string, AdoptionRecord>;

async function readAdoptions(repos: Repositories): Promise<AdoptionsByUser> {
  const row = await repos.settings.get(ADOPTION_KEY);
  const value = row?.value as AdoptionsByUser | undefined;
  return value && typeof value === 'object' ? value : {};
}

/**
 * What is on this iPad that this account has not already taken a copy of.
 *
 * Keyed by user so that two learners sharing an iPad each get asked about the
 * work they made, and neither is offered the other's.
 */
export async function summariseLocalWork(
  repos: Repositories,
  progress: Record<string, LessonProgress>,
  userId: string,
): Promise<LocalWorkSummary> {
  const adoptions = await readAdoptions(repos);
  const mine = adoptions[userId];
  const doneDocs = new Set(mine?.documentIds ?? []);
  const doneLessons = new Set(mine?.lessonIds ?? []);

  const documents = (await repos.documents.list())
    .filter((doc) => !doneDocs.has(doc.id))
    // Opening the writing surface creates an empty document before a single
    // stroke is drawn, so without this every learner is offered an "Untitled"
    // with nothing in it — noise in a list whose whole job is to be a truthful
    // account of what exists. A document with no ink has no handwriting to
    // preserve; it stays on the iPad and is offered the moment it has any.
    .filter((doc) => doc.payload.strokes.length > 0)
    .map((doc) => ({
      id: doc.id,
      title: doc.title,
      feature: doc.feature,
      updatedAt: doc.updatedAt,
    }));

  // Only lessons actually practised are worth carrying; an untouched lesson has
  // nothing to say.
  const lessons = Object.values(progress)
    .filter((entry) => entry.lastPractisedAt !== null && !doneLessons.has(entry.lessonId))
    .map((entry) => entry.lessonId);

  return { documents, lessons, total: documents.length + lessons.length };
}

export interface AdoptionResult {
  documentsCopied: number;
  lessonsCopied: number;
  /** Already on the server and identical, so nothing needed sending. */
  alreadyThere: number;
  /** Items that did not make it. They stay pending and can be retried. */
  failed: number;
}

export interface AdoptionOptions {
  signal?: AbortSignal;
  /** Called after each item so a long copy can show honest progress. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Queue the listed work for the signed-in account.
 *
 * Pulls first. That is the compatibility migration: a learner who used the
 * earlier direct-copy build already has these documents on the server under the
 * same UUIDs, and the pull binds each one to its remote revision. Anything that
 * comes back identical is recorded as adopted without being sent again — no
 * duplicate, no needless revision, and no conflict manufactured out of work that
 * already agreed.
 *
 * Only what the learner approved is enqueued. Declined work has no binding and
 * never reaches the outbox.
 */
export async function adoptLocalWork(
  engine: SyncEngine,
  repos: Repositories,
  progress: Record<string, LessonProgress>,
  userId: string,
  summary: LocalWorkSummary,
  options: AdoptionOptions = {},
): Promise<AdoptionResult> {
  // Find out what the account already holds before sending anything.
  await engine.run();

  const adoptedDocs: string[] = [];
  const adoptedLessons: string[] = [];
  let alreadyThere = 0;
  let queuedDocuments = 0;
  let queuedLessons = 0;
  let done = 0;
  const total = summary.total;

  const step = () => {
    done += 1;
    options.onProgress?.(done, total);
  };

  for (const item of summary.documents) {
    const document = await repos.documents.get(item.id);
    if (!document) {
      // Deleted locally between the summary and the confirmation. Nothing to
      // copy, and nothing to report as a failure.
      step();
      continue;
    }

    if (await engine.isBound('document', document.id)) {
      // The pull above already found this record on the server. Either it was
      // identical — the state the old direct-copy build left behind — or it has
      // diverged and a conflict is already recorded. Enqueuing would duplicate
      // effort in the first case and manufacture a second conflict in the other.
      alreadyThere += 1;
      adoptedDocs.push(document.id);
      step();
      continue;
    }

    await engine.enqueue({
      kind: 'document',
      recordId: document.id,
      payload: document.payload as unknown as Record<string, unknown>,
      title: document.title,
      feature: document.feature,
      schemaVersion: document.schemaVersion,
    });
    adoptedDocs.push(document.id);
    queuedDocuments += 1;
    step();
  }

  for (const lessonId of summary.lessons) {
    const entry = progress[lessonId];
    if (!entry) {
      step();
      continue;
    }
    if (await engine.isBound('lesson_progress', lessonId)) {
      alreadyThere += 1;
      adoptedLessons.push(lessonId);
      step();
      continue;
    }
    await engine.enqueue({
      kind: 'lesson_progress',
      recordId: lessonId,
      payload: entry as unknown as Record<string, unknown>,
    });
    adoptedLessons.push(lessonId);
    queuedLessons += 1;
    step();
  }

  await recordAdoption(repos, userId, adoptedDocs, adoptedLessons);

  // Send what was queued. Anything still queued afterwards is retried by the
  // engine on its own schedule, so a partial failure is not the learner's problem.
  await engine.run();
  const stillQueued = engine.current().pending;

  return {
    documentsCopied: queuedDocuments,
    lessonsCopied: queuedLessons,
    alreadyThere,
    failed: stillQueued,
  };
}

/**
 * Remember what this account has taken, so the learner is not asked twice.
 *
 * Merged rather than replaced: a second adoption after a partial failure must
 * add to the record, not overwrite what already succeeded.
 */
export async function recordAdoption(
  repos: Repositories,
  userId: string,
  documentIds: string[],
  lessonIds: string[],
): Promise<void> {
  const adoptions = await readAdoptions(repos);
  const existing = adoptions[userId];
  adoptions[userId] = {
    documentIds: [...new Set([...(existing?.documentIds ?? []), ...documentIds])],
    lessonIds: [...new Set([...(existing?.lessonIds ?? []), ...lessonIds])],
    at: Date.now(),
  };
  await repos.settings.set(ADOPTION_KEY, adoptions as unknown as Record<string, unknown>);
}

/**
 * Record the learner's decision to keep this work local, without copying it.
 *
 * "No" has to be remembered too, or the question returns on every sign-in and
 * becomes something to dismiss rather than answer.
 */
export async function declineAdoption(
  repos: Repositories,
  userId: string,
  summary: LocalWorkSummary,
): Promise<void> {
  await recordAdoption(
    repos,
    userId,
    summary.documents.map((doc) => doc.id),
    summary.lessons,
  );
}
